import * as XLSX from 'xlsx';
import { 
  RelatorioAdminResponse, 
  RelatorioLiderResponse, 
  RelatorioUsuarioResponse,
  FiltrosRelatorio
} from './relatorioService';

/**
 * Serviço para exportação de relatórios em formato Excel (.xlsx)
 */

/**
 * Formata filtros para exibição no Excel
 */
const formatarFiltrosParaExcel = (filtros?: FiltrosRelatorio): any[] => {
  if (!filtros) return [];
  
  const linhasFiltros: any[] = [];
  
  if (filtros.dataInicio || filtros.dataFim) {
    linhasFiltros.push(['Período Filtrado', `${filtros.dataInicio || 'Início'} até ${filtros.dataFim || 'Fim'}`]);
  }
  
  if (filtros.departamentos && filtros.departamentos.length > 0) {
    linhasFiltros.push(['Departamentos', filtros.departamentos.join(', ')]);
  }
  
  if (filtros.status && filtros.status.length > 0) {
    linhasFiltros.push(['Status', filtros.status.join(', ')]);
  }
  
  if (filtros.prioridades && filtros.prioridades.length > 0) {
    linhasFiltros.push(['Prioridades', filtros.prioridades.join(', ')]);
  }
  
  if (filtros.usuarios && filtros.usuarios.length > 0) {
    linhasFiltros.push(['Usuários Filtrados', `${filtros.usuarios.length} usuário(s)`]);
  }
  
  return linhasFiltros;
};

/**
 * Exporta relatório administrativo para Excel
 */
export const exportarRelatorioAdmin = (relatorio: RelatorioAdminResponse, filtros?: FiltrosRelatorio) => {
  console.log('📊 [exportacaoService] Exportando relatório admin');
  console.log('📊 [exportacaoService] Filtros recebidos:', filtros);
  console.log('📊 [exportacaoService] Quantidade de departamentos no relatório:', relatorio.porDepartamento?.length);
  console.log('📊 [exportacaoService] Total de departamentos no resumo:', relatorio.resumoGeral.totalDepartamentos);
  console.log('📊 [exportacaoService] Departamentos:', relatorio.porDepartamento?.map(d => d.nome));
  
  const workbook = XLSX.utils.book_new();

  // Aba 1: Resumo Geral
  const periodoLabel = relatorio.periodo.filtroData
    ? `${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`
    : 'Todos os períodos';

  const resumoData: any[] = [
    ['RESUMO GERAL'],
    ['Período', periodoLabel],
    []
  ];
  
  // Adicionar informações de filtros se houver
  const filtrosFormatados = formatarFiltrosParaExcel(filtros);
  if (filtrosFormatados.length > 0) {
    resumoData.push(['FILTROS APLICADOS']);
    filtrosFormatados.forEach(linha => resumoData.push(linha));
    resumoData.push([]);
  }
  
  resumoData.push(
    ['Métrica', 'Valor'],
    ['Total de Usuários', relatorio.resumoGeral.totalUsuarios],
    ['Total de Departamentos', relatorio.resumoGeral.totalDepartamentos],
    ['Total de Tarefas', relatorio.resumoGeral.totalTarefas],
    ['Tarefas Concluídas', relatorio.resumoGeral.tarefasConcluidas],
    ['Tarefas Concluídas com Atraso', relatorio.resumoGeral.tarefasConcluidasComAtraso],
    ['Tarefas Pendentes', relatorio.resumoGeral.tarefasPendentes],
    ['Tarefas Atrasadas', relatorio.resumoGeral.tarefasAtrasadas],
    ['Taxa de Conclusão Geral', `${Math.round(relatorio.resumoGeral.taxaConclusaoGeral)}%`],
    ['Tarefas Mais Antigas (Atenção)', relatorio.tarefasMaisAntigas?.length ?? 0]
  );
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo Geral');

  // Aba 2: Performance por Departamento
  const deptData: any[] = [
    ['PERFORMANCE POR DEPARTAMENTO'],
    [],
    ['Departamento', 'Total Tarefas', 'Concluídas', 'Concluídas com Atraso', 'Atrasadas', 'Usuários Ativos', 'Taxa de Conclusão']
  ];
  relatorio.porDepartamento.forEach(dept => {
    deptData.push([
      dept.nome,
      dept.totalTarefas,
      dept.tarefasConcluidas,
      dept.tarefasConcluidasComAtraso ?? 0,
      dept.tarefasAtrasadas,
      dept.usuariosAtivos,
      `${Math.round(dept.taxaConclusao)}%`
    ]);
  });
  const wsDept = XLSX.utils.aoa_to_sheet(deptData);
  XLSX.utils.book_append_sheet(workbook, wsDept, 'Por Departamento');

  // Aba 3: Top Usuários
  const topUsuariosData: any[] = [
    ['TOP 10 USUÁRIOS MAIS PRODUTIVOS'],
    [],
    ['Posição', 'Nome', 'Departamento', 'Total Tarefas', 'Concluídas', 'Taxa de Conclusão']
  ];
  relatorio.topUsuarios.forEach((usuario, index) => {
    topUsuariosData.push([
      `#${index + 1}`,
      usuario.nome,
      usuario.departamento,
      usuario.totalTarefas,
      usuario.tarefasConcluidas,
      `${Math.round(usuario.taxaConclusao)}%`
    ]);
  });
  const wsTopUsuarios = XLSX.utils.aoa_to_sheet(topUsuariosData);
  XLSX.utils.book_append_sheet(workbook, wsTopUsuarios, 'Top Usuários');

  // Aba 4: Tarefas Atrasadas
  const atrasadasData: any[] = [
    ['TAREFAS ATRASADAS POR DEPARTAMENTO'],
    [],
    ['Total de Tarefas Atrasadas', relatorio.tarefasAtrasadas.total],
    [],
    ['Departamento', 'Quantidade']
  ];
  Object.entries(relatorio.tarefasAtrasadas.porDepartamento).forEach(([dept, qtd]) => {
    atrasadasData.push([dept, qtd]);
  });
  const wsAtrasadas = XLSX.utils.aoa_to_sheet(atrasadasData);
  XLSX.utils.book_append_sheet(workbook, wsAtrasadas, 'Tarefas Atrasadas');

  // Aba 5: Tendências Mensais
  if (relatorio.tendenciasMensais && relatorio.tendenciasMensais.length > 0) {
    const tendenciasData: any[] = [
      ['TENDÊNCIAS MENSAIS'],
      [],
      ['Mês', 'Criadas', 'Concluídas', 'Taxa de Conclusão']
    ];
    relatorio.tendenciasMensais.forEach(item => {
      const taxa = item.criadas > 0 ? Math.round((item.concluidas / item.criadas) * 100) : 0;
      tendenciasData.push([
        item.mes,
        item.criadas,
        item.concluidas,
        `${taxa}%`
      ]);
    });
    const wsTendencias = XLSX.utils.aoa_to_sheet(tendenciasData);
    XLSX.utils.book_append_sheet(workbook, wsTendencias, 'Tendências Mensais');
  }

  // Aba 6: Tempo Médio por Status (se disponível)
  if (relatorio.tempoMedioPorStatus && relatorio.tempoMedioPorStatus.length > 0) {
    const tempoStatusData: any[] = [
      ['TEMPO MÉDIO POR STATUS'],
      [],
      ['Status', 'Tempo Médio (dias)', 'Total Tarefas', 'Mais Rápida (dias)', 'Mais Lenta (dias)']
    ];
    relatorio.tempoMedioPorStatus.forEach(item => {
      tempoStatusData.push([
        item.status,
        typeof item.tempoMedio === 'number' ? item.tempoMedio.toFixed(1) : item.tempoMedio,
        item.totalTarefas,
        typeof item.maisRapida === 'number' ? item.maisRapida.toFixed(1) : item.maisRapida,
        typeof item.maisLenta === 'number' ? item.maisLenta.toFixed(1) : item.maisLenta
      ]);
    });
    const wsTempoStatus = XLSX.utils.aoa_to_sheet(tempoStatusData);
    XLSX.utils.book_append_sheet(workbook, wsTempoStatus, 'Tempo por Status');
  }

  // Aba 7: Tarefas Mais Antigas (se disponível)
  if (relatorio.tarefasMaisAntigas && relatorio.tarefasMaisAntigas.length > 0) {
    const antigasData: any[] = [
      ['TAREFAS MAIS ANTIGAS (ATENÇÃO)'],
      [],
      ['#', 'Tarefa', 'Departamento', 'Responsável', 'Status', 'Prioridade', 'Dias Aberta']
    ];
    relatorio.tarefasMaisAntigas.forEach((tarefa, index) => {
      antigasData.push([
        index + 1,
        tarefa.titulo,
        tarefa.departamento,
        tarefa.responsavel,
        tarefa.status,
        tarefa.prioridade,
        tarefa.diasAberta
      ]);
    });
    const wsAntigas = XLSX.utils.aoa_to_sheet(antigasData);
    wsAntigas['!cols'] = [
      { wch: 5 },
      { wch: 45 },
      { wch: 25 },
      { wch: 30 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(workbook, wsAntigas, 'Tarefas Mais Antigas');
  }

  // Aba 8: Produtividade Detalhada (se disponível)
  if (relatorio.produtividadeDetalhada && relatorio.produtividadeDetalhada.length > 0) {
    const prodData: any[] = [
      ['PRODUTIVIDADE DETALHADA'],
      [],
      ['Nome', 'Departamento', 'Ativas', 'Concluídas', 'Atrasadas', 'Tempo Médio (dias)', 'Taxa Conclusão', 'Score']
    ];
    relatorio.produtividadeDetalhada.forEach(item => {
      prodData.push([
        item.nome,
        item.departamento,
        item.tarefasAtivas,
        item.tarefasConcluidas,
        item.tarefasAtrasadas,
        typeof item.tempoMedioConclusao === 'number' ? item.tempoMedioConclusao.toFixed(1) : item.tempoMedioConclusao,
        `${Math.round(item.taxaConclusao)}%`,
        typeof item.produtividadeScore === 'number' ? item.produtividadeScore.toFixed(1) : item.produtividadeScore
      ]);
    });
    const wsProd = XLSX.utils.aoa_to_sheet(prodData);
    XLSX.utils.book_append_sheet(workbook, wsProd, 'Produtividade');
  }

  // Gerar arquivo com nome diferente se houver filtros
  const sufixoData = relatorio.periodo.filtroData
    ? `${relatorio.periodo.dataInicio}_${relatorio.periodo.dataFim}`
    : 'Todos_os_Periodos';
  const nomeArquivo = filtros && Object.keys(filtros).some(k => (filtros as any)[k])
    ? `Relatorio_Administrativo_Filtrado_${sufixoData}.xlsx`
    : `Relatorio_Administrativo_Geral_${sufixoData}.xlsx`;
  
  console.log(' [exportacaoService] Salvando arquivo:', nomeArquivo);
  XLSX.writeFile(workbook, nomeArquivo);
};

/**
 * Exporta relatório de líder para Excel
 */
export const exportarRelatorioLider = (relatorio: RelatorioLiderResponse, filtros?: FiltrosRelatorio) => {
  const workbook = XLSX.utils.book_new();

  // Aba 1: Resumo
  const resumoData: any[] = [
    ['RELATÓRIO DO DEPARTAMENTO'],
    ['Departamento', relatorio.departamento.nome],
    ['Período', `${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`],
    []
  ];
  
  // Adicionar informações de filtros se houver
  const filtrosFormatados = formatarFiltrosParaExcel(filtros);
  if (filtrosFormatados.length > 0) {
    resumoData.push(['FILTROS APLICADOS']);
    filtrosFormatados.forEach(linha => resumoData.push(linha));
    resumoData.push([]);
  }
  
  resumoData.push(
    ['Métrica', 'Valor'],
    ['Total de Tarefas', relatorio.resumo.totalTarefas],
    ['Tarefas Concluídas', relatorio.resumo.tarefasConcluidas],
    ['Tarefas Pendentes', relatorio.resumo.tarefasPendentes],
    ['Tarefas Atrasadas', relatorio.resumo.tarefasAtrasadas],
    ['Taxa de Conclusão', `${Math.round(relatorio.resumo.taxaConclusao)}%`],
    ['Tempo Médio de Conclusão', relatorio.resumo.tempoMedioConclusao]
  );
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo');

  // Aba 2: Por Status
  const statusData: any[] = [
    ['TAREFAS POR STATUS'],
    [],
    ['Status', 'Quantidade']
  ];
  relatorio.porStatus.forEach(item => {
    statusData.push([item.status, item.quantidade]);
  });
  const wsStatus = XLSX.utils.aoa_to_sheet(statusData);
  XLSX.utils.book_append_sheet(workbook, wsStatus, 'Por Status');

  // Aba 3: Por Prioridade
  const prioridadeData: any[] = [
    ['TAREFAS POR PRIORIDADE'],
    [],
    ['Prioridade', 'Quantidade']
  ];
  relatorio.porPrioridade.forEach(item => {
    prioridadeData.push([item.prioridade, item.quantidade]);
  });
  const wsPrioridade = XLSX.utils.aoa_to_sheet(prioridadeData);
  XLSX.utils.book_append_sheet(workbook, wsPrioridade, 'Por Prioridade');

  // Aba 4: Performance por Usuário
  const usuariosData: any[] = [
    ['PERFORMANCE POR USUÁRIO'],
    [],
    ['Nome', 'Ativas', 'Concluídas', 'Atrasadas', 'Taxa de Conclusão']
  ];
  relatorio.porUsuario.forEach(usuario => {
    usuariosData.push([
      usuario.nome,
      usuario.tarefasAtivas,
      usuario.tarefasConcluidas,
      usuario.tarefasAtrasadas,
      `${Math.round(usuario.taxaConclusao)}%`
    ]);
  });
  const wsUsuarios = XLSX.utils.aoa_to_sheet(usuariosData);
  XLSX.utils.book_append_sheet(workbook, wsUsuarios, 'Por Usuário');

  // Gerar arquivo e fazer download
  const nomeArquivo = `Relatorio_Departamento_${relatorio.departamento.nome}_${relatorio.periodo.dataInicio}_${relatorio.periodo.dataFim}.xlsx`;
  XLSX.writeFile(workbook, nomeArquivo);
};

/**
 * Exporta relatório pessoal para Excel
 */
export const exportarRelatorioUsuario = (relatorio: RelatorioUsuarioResponse) => {
  const workbook = XLSX.utils.book_new();

  // Aba 1: Resumo
  const resumoData: any[] = [
    ['MEU RELATÓRIO PESSOAL'],
    ['Período', `${relatorio.periodo.dataInicio} até ${relatorio.periodo.dataFim}`],
    [],
    ['Métrica', 'Valor'],
    ['Total de Tarefas', relatorio.resumo.totalTarefas],
    ['Tarefas Concluídas', relatorio.resumo.tarefasConcluidas],
    ['Tarefas Pendentes', relatorio.resumo.tarefasPendentes],
    ['Tarefas Atrasadas', relatorio.resumo.tarefasAtrasadas],
    ['Taxa de Conclusão', `${Math.round(relatorio.resumo.taxaConclusao)}%`],
    ['Tempo Médio de Conclusão', relatorio.resumo.tempoMedioConclusao]
  ];
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo');

  // Aba 2: Por Status
  const statusData: any[] = [
    ['MINHAS TAREFAS POR STATUS'],
    [],
    ['Status', 'Quantidade']
  ];
  relatorio.porStatus.forEach(item => {
    statusData.push([item.status, item.quantidade]);
  });
  const wsStatus = XLSX.utils.aoa_to_sheet(statusData);
  XLSX.utils.book_append_sheet(workbook, wsStatus, 'Por Status');

  // Aba 3: Por Prioridade
  const prioridadeData: any[] = [
    ['MINHAS TAREFAS POR PRIORIDADE'],
    [],
    ['Prioridade', 'Quantidade']
  ];
  relatorio.porPrioridade.forEach(item => {
    prioridadeData.push([item.prioridade, item.quantidade]);
  });
  const wsPrioridade = XLSX.utils.aoa_to_sheet(prioridadeData);
  XLSX.utils.book_append_sheet(workbook, wsPrioridade, 'Por Prioridade');

  // Gerar arquivo e fazer download
  const nomeArquivo = `Meu_Relatorio_${relatorio.periodo.dataInicio}_${relatorio.periodo.dataFim}.xlsx`;
  XLSX.writeFile(workbook, nomeArquivo);
};

/**
 * Exporta métricas de colaborador para Excel
 */
export const exportarMetricasDepartamento = (dados: {
  departamento: { nome: string; descricao: string };
  metricas: any;
  colaboradores?: Array<{
    nome: string;
    cargo: string;
    metricas: {
      totalTarefas: number;
      tarefasConcluidas: number;
      tarefasPendentes: number;
      tarefasAtrasadas: number;
      taxaConclusao: number;
    };
  }>;
  periodo?: { dataInicio: string | null; dataFim: string | null };
  filtros?: FiltrosRelatorio;
}) => {
  const workbook = XLSX.utils.book_new();
  
  // Aba 1: Resumo do Departamento
  const resumoData: any[] = [
    ['RELATÓRIO DE MÉTRICAS DO DEPARTAMENTO'],
    [],
    ['INFORMAÇÕES DO DEPARTAMENTO'],
    ['Nome:', dados.departamento.nome],
    ['Descrição:', dados.departamento.descricao || 'Sem descrição'],
    [],
    ['PERÍODO ANALISADO'],
    ['Todas as tarefas (sem filtro de data)'],
    [],
    ['RESUMO GERAL DO DEPARTAMENTO'],
    [],
    ['Métrica', 'Quantidade', 'Percentual'],
    ['Total de Tarefas', dados.metricas.totalTarefas, '100%'],
    ['Tarefas Concluídas', dados.metricas.tarefasConcluidas, `${dados.metricas.taxaConclusao}%`],
    ['  • Concluídas no Prazo', dados.metricas.tarefasConcluidasNoPrazo, `${dados.metricas.totalTarefas > 0 ? Math.round((dados.metricas.tarefasConcluidasNoPrazo / dados.metricas.totalTarefas) * 100) : 0}%`],
    ['  • Concluídas com Atraso', dados.metricas.tarefasConcluidasComAtraso, `${dados.metricas.totalTarefas > 0 ? Math.round((dados.metricas.tarefasConcluidasComAtraso / dados.metricas.totalTarefas) * 100) : 0}%`],
    ['Tarefas Pendentes', dados.metricas.tarefasPendentes, `${dados.metricas.totalTarefas > 0 ? Math.round((dados.metricas.tarefasPendentes / dados.metricas.totalTarefas) * 100) : 0}%`],
    ['Tarefas Atrasadas', dados.metricas.tarefasAtrasadas, `${dados.metricas.totalTarefas > 0 ? Math.round((dados.metricas.tarefasAtrasadas / dados.metricas.totalTarefas) * 100) : 0}%`],
    [],
    ['INDICADORES DE PERFORMANCE'],
    [],
    ['Indicador', 'Valor', 'Status'],
    ['Taxa de Conclusão', `${dados.metricas.taxaConclusao}%`, dados.metricas.taxaConclusao >= 80 ? 'Excelente' : dados.metricas.taxaConclusao >= 60 ? 'Bom' : 'Precisa Melhorar'],
    ['Tempo Médio de Conclusão', `${dados.metricas.tempoMedioConclusao} dias`, parseFloat(dados.metricas.tempoMedioConclusao) <= 7 ? 'Rápido' : parseFloat(dados.metricas.tempoMedioConclusao) <= 14 ? 'Normal' : 'Lento']
  ];
  
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  wsResumo['!cols'] = [
    { wch: 35 },
    { wch: 20 },
    { wch: 15 }
  ];
  XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo do Departamento');
  
  // Aba 2: Colaboradores do Departamento
  if (dados.colaboradores && dados.colaboradores.length > 0) {
    const colaboradoresData: any[] = [
      ['COLABORADORES DO DEPARTAMENTO'],
      [],
      ['Colaborador', 'Cargo', 'Total Tarefas', 'Concluídas', 'Pendentes', 'Atrasadas', 'Taxa Conclusão', 'Performance'],
      []
    ];
    
    dados.colaboradores.forEach(col => {
      const performance = col.metricas.taxaConclusao >= 80 ? 'Excelente' : 
                         col.metricas.taxaConclusao >= 60 ? 'Bom' : 'Precisa Melhorar';
      
      colaboradoresData.push([
        col.nome,
        col.cargo,
        col.metricas.totalTarefas,
        col.metricas.tarefasConcluidas,
        col.metricas.tarefasPendentes,
        col.metricas.tarefasAtrasadas,
        `${col.metricas.taxaConclusao}%`,
        performance
      ]);
    });
    
    colaboradoresData.push([]);
    colaboradoresData.push(['ESTATÍSTICAS DOS COLABORADORES']);
    colaboradoresData.push([]);
    colaboradoresData.push(['Total de Colaboradores:', dados.colaboradores.length]);
    colaboradoresData.push(['Média de Tarefas por Colaborador:', Math.round(dados.colaboradores.reduce((sum, col) => sum + col.metricas.totalTarefas, 0) / dados.colaboradores.length)]);
    colaboradoresData.push(['Taxa Média de Conclusão:', `${Math.round(dados.colaboradores.reduce((sum, col) => sum + col.metricas.taxaConclusao, 0) / dados.colaboradores.length)}%`]);
    
    const wsColaboradores = XLSX.utils.aoa_to_sheet(colaboradoresData);
    wsColaboradores['!cols'] = [
      { wch: 30 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 18 }
    ];
    XLSX.utils.book_append_sheet(workbook, wsColaboradores, 'Colaboradores');
  }
  
  // Gerar arquivo
  const nomeArquivo = `Metricas_Departamento_${dados.departamento.nome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, nomeArquivo);
};

export const exportarMetricasColaborador = (dados: {
  colaborador: { nome: string; email: string; departamento: string };
  metricas: any;
  periodo?: { dataInicio: string; dataFim: string };
  filtros?: FiltrosRelatorio;
}) => {
  const workbook = XLSX.utils.book_new();
  
  // Aba: Métricas do Colaborador
  const metricasData: any[] = [
    ['RELATÓRIO DE MÉTRICAS DO COLABORADOR'],
    [],
    ['INFORMAÇÕES DO COLABORADOR'],
    ['Nome:', dados.colaborador.nome],
    ['Email:', dados.colaborador.email],
    ['Departamento:', dados.colaborador.departamento],
    []
  ];
  
  // Período
  if (dados.periodo && (dados.periodo.dataInicio || dados.periodo.dataFim)) {
    metricasData.push(['PERÍODO ANALISADO']);
    metricasData.push(['Data Início:', dados.periodo.dataInicio || 'Sem filtro']);
    metricasData.push(['Data Fim:', dados.periodo.dataFim || 'Sem filtro']);
    metricasData.push([]);
  } else {
    metricasData.push(['PERÍODO ANALISADO']);
    metricasData.push(['Todas as tarefas (sem filtro de data)']);
    metricasData.push([]);
  }
  
  // Filtros aplicados
  if (dados.filtros) {
    const filtrosFormatados = formatarFiltrosParaExcel(dados.filtros);
    if (filtrosFormatados.length > 0) {
      metricasData.push(['FILTROS APLICADOS']);
      metricasData.push(...filtrosFormatados);
      metricasData.push([]);
    }
  }
  
  // Resumo de tarefas em formato de tabela
  metricasData.push(['RESUMO DE TAREFAS']);
  metricasData.push(['Métrica', 'Quantidade']);
  metricasData.push(['Total de Tarefas', dados.metricas.totalTarefas]);
  metricasData.push(['Tarefas Concluídas', dados.metricas.tarefasConcluidas]);
  metricasData.push(['  - Concluídas no Prazo', dados.metricas.tarefasConcluidasNoPrazo]);
  metricasData.push(['  - Concluídas com Atraso', dados.metricas.tarefasConcluidasComAtraso]);
  metricasData.push(['Tarefas Pendentes', dados.metricas.tarefasPendentes]);
  metricasData.push(['Tarefas Atrasadas', dados.metricas.tarefasAtrasadas]);
  metricasData.push([]);
  
  // Indicadores de performance
  metricasData.push(['INDICADORES DE PERFORMANCE']);
  metricasData.push(['Indicador', 'Valor']);
  metricasData.push(['Taxa de Conclusão', `${dados.metricas.taxaConclusao}%`]);
  metricasData.push(['Tempo Médio de Conclusão', `${dados.metricas.tempoMedioConclusao} dias`]);
  
  const wsMetricas = XLSX.utils.aoa_to_sheet(metricasData);
  
  // Aplicar largura das colunas
  wsMetricas['!cols'] = [
    { wch: 35 }, // Coluna A - Descrições
    { wch: 30 }  // Coluna B - Valores
  ];
  
  XLSX.utils.book_append_sheet(workbook, wsMetricas, 'Métricas do Colaborador');
  
  // Gerar arquivo
  const nomeArquivo = `Metricas_${dados.colaborador.nome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, nomeArquivo);
};

const formatarSegundos = (segundos: number): string => {
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const COLS_RESUMO = [
  { wch: 8 }, { wch: 40 }, { wch: 20 }, { wch: 18 }, { wch: 12 },
  { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 24 }, { wch: 12 }, { wch: 35 }, { wch: 10 }
];

const montarLinhasResumo = (tarefas: any[]): any[][] => {
  const cabecalho = [
    'ID', 'Título', 'Departamento', 'Status', 'Prioridade',
    'Criação', 'Vencimento', 'Conclusão',
    'Tempo no Departamento (~ = aproximado)', 'Tempo Total', 'Participantes', 'Mudanças de Status'
  ];
  const linhas: any[][] = [cabecalho];
  tarefas.forEach((t) => {
    // "~" antes do tempo indica APROXIMAÇÃO: a tarefa nunca foi transferida
    // de departamento desde que esse rastreamento passou a existir (ver
    // pipebravo.historico_departamento) — o valor cai no fallback da data
    // de criação e por isso empata com o Tempo Total, mesmo que a tarefa já
    // tenha mudado de departamento ANTES desse rastreamento existir.
    const tempoNoDepto = formatarSegundos(Number(t.tempo_no_departamento_segundos) || 0);
    const ehAproximado = !t.tem_historico_departamento;
    linhas.push([
      t.id,
      t.titulo,
      t.departamento_nome || '',
      t.status || '',
      t.prioridade || '',
      t.data_criacao ? new Date(t.data_criacao).toLocaleDateString('pt-BR') : '',
      t.data_vencimento ? new Date(t.data_vencimento).toLocaleDateString('pt-BR') : '',
      t.data_conclusao ? new Date(t.data_conclusao).toLocaleDateString('pt-BR') : '',
      ehAproximado ? `~${tempoNoDepto}` : tempoNoDepto,
      formatarSegundos(Number(t.tempo_total_segundos) || 0),
      t.participantes || '',
      Number(t.total_mudancas_status) || 0
    ]);
  });
  return linhas;
};

const exportarRelatorioTemporizador = (dados: {
  tarefas: any[];
  tempoResponsavel: any[];
  etapas: any[];
  filtros?: { departamento?: string; dataInicio?: string; dataFim?: string; status?: string };
}) => {
  const workbook = XLSX.utils.book_new();
  const filtrouDepartamento = !!dados.filtros?.departamento;

  if (filtrouDepartamento) {
    // ─── Modo departamento único: aba única de Resumo ───
    const resumoData: any[][] = [];
    resumoData.push(['RELATÓRIO DO TEMPORIZADOR - RESUMO POR TAREFA']);
    resumoData.push([`Departamento: ${dados.filtros!.departamento}`]);
    if (dados.filtros?.dataInicio || dados.filtros?.dataFim)
      resumoData.push([`Período: ${dados.filtros.dataInicio || '—'} a ${dados.filtros.dataFim || '—'}`]);
    resumoData.push([]);
    montarLinhasResumo(dados.tarefas).forEach((l) => resumoData.push(l));

    const ws = XLSX.utils.aoa_to_sheet(resumoData);
    ws['!cols'] = COLS_RESUMO;
    XLSX.utils.book_append_sheet(workbook, ws, 'Resumo por Tarefa');
  } else {
    // ─── Modo todos os departamentos: uma aba por departamento ───
    const porDepto = new Map<string, any[]>();
    dados.tarefas.forEach((t) => {
      const nome = t.departamento_nome || 'Sem Departamento';
      if (!porDepto.has(nome)) porDepto.set(nome, []);
      porDepto.get(nome)!.push(t);
    });

    // Aba "Geral" com todas as tarefas
    const geralData: any[][] = [['RELATÓRIO DO TEMPORIZADOR - TODOS OS DEPARTAMENTOS'], []];
    montarLinhasResumo(dados.tarefas).forEach((l) => geralData.push(l));
    const wsGeral = XLSX.utils.aoa_to_sheet(geralData);
    wsGeral['!cols'] = COLS_RESUMO;
    XLSX.utils.book_append_sheet(workbook, wsGeral, 'Geral');

    // Uma aba por departamento (limite Excel: 31 chars, sem : \ / ? * [ ])
    const nomesUsados = new Set<string>();
    porDepto.forEach((tarefas, deptoNome) => {
      let nomeAba = deptoNome
        .replace(/[:\\/\?\*\[\]]/g, '-')
        .substring(0, 28)
        .trim();
      if (nomesUsados.has(nomeAba)) nomeAba = nomeAba.substring(0, 25) + '_' + nomesUsados.size;
      nomesUsados.add(nomeAba);

      const data: any[][] = [[`DEPARTAMENTO: ${deptoNome}`], []];
      montarLinhasResumo(tarefas).forEach((l) => data.push(l));
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = COLS_RESUMO;
      XLSX.utils.book_append_sheet(workbook, ws, nomeAba);
    });
  }

  // ─── Tempo por Responsável (sempre aba única) ───
  const tempoData: any[][] = [['TEMPO POR RESPONSÁVEL'], [], ['Tarefa', 'Departamento', 'Responsável', 'Início', 'Fim', 'Duração']];
  dados.tempoResponsavel.forEach((tr) => {
    tempoData.push([
      tr.tarefa_titulo || '',
      tr.departamento_nome || '',
      tr.responsavel_nome || '',
      tr.inicio ? new Date(tr.inicio).toLocaleString('pt-BR') : '',
      tr.fim ? new Date(tr.fim).toLocaleString('pt-BR') : '',
      formatarSegundos(Number(tr.duracao_segundos) || 0)
    ]);
  });
  const wsTempo = XLSX.utils.aoa_to_sheet(tempoData);
  wsTempo['!cols'] = [{ wch: 40 }, { wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, wsTempo, 'Tempo por Responsável');

  // ─── Histórico de Etapas (sempre aba única) ───
  const etapasData: any[][] = [['HISTÓRICO DE ETAPAS'], [], ['Tarefa', 'Departamento', 'Status Anterior', 'Status Novo', 'Data/Hora', 'Alterado Por']];
  dados.etapas.forEach((e) => {
    etapasData.push([
      e.tarefa_titulo || '',
      e.departamento_nome || '',
      e.status_anterior || '',
      e.status_novo || '',
      e.data_hora ? new Date(e.data_hora).toLocaleString('pt-BR') : '',
      e.usuario_nome || ''
    ]);
  });
  const wsEtapas = XLSX.utils.aoa_to_sheet(etapasData);
  wsEtapas['!cols'] = [{ wch: 40 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 25 }];
  XLSX.utils.book_append_sheet(workbook, wsEtapas, 'Histórico de Etapas');

  const nomeArquivo = `Relatorio_Temporizador_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, nomeArquivo);
};

const exportacaoService = {
  exportarRelatorioAdmin,
  exportarRelatorioLider,
  exportarRelatorioUsuario,
  exportarMetricasColaborador,
  exportarRelatorioTemporizador
};

export default exportacaoService;
